import { useCallback, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { FeedbackAnchors, ReportFeedback } from '@/types/report-feedback';

interface UseReportFeedbackReturn {
  feedbackHistory: ReportFeedback[];
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  submitFeedback: (
    reportId: string,
    feedback: string,
    context?: string,
    anchors?: FeedbackAnchors | null
  ) => Promise<void>;
  fetchFeedback: (reportId: string) => Promise<void>;
}

/**
 * Hook for managing report feedback.
 *
 * Handles submitting feedback (which triggers report re-composition)
 * and fetching feedback history for a report.
 */
export function useReportFeedback(): UseReportFeedbackReturn {
  const [feedbackHistory, setFeedbackHistory] = useState<ReportFeedback[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFeedback = useCallback(async (reportId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await vnextApiClient.getReportFeedback(reportId);
      setFeedbackHistory(response);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch feedback';
      setError(msg);
      Sentry.captureException(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const submitFeedback = useCallback(
    async (
      reportId: string,
      feedback: string,
      context?: string,
      anchors?: FeedbackAnchors | null
    ) => {
      setIsSubmitting(true);
      setError(null);
      try {
        const response = await vnextApiClient.submitReportFeedback(
          reportId,
          feedback,
          context,
          anchors
        );
        setFeedbackHistory((prev) => [...prev, response]);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to submit feedback';
        setError(msg);
        Sentry.captureException(err);
      } finally {
        setIsSubmitting(false);
      }
    },
    []
  );

  return {
    feedbackHistory,
    isLoading,
    isSubmitting,
    error,
    submitFeedback,
    fetchFeedback,
  };
}
